import { Component, Input, computed, effect, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { RegisterComponent } from '../component-lookup';
import { StateService } from '../../services/state.service';
import { InstitutionAutocompleteComponent } from '../institution-autocomplete/institution-autocomplete.component';
import { Institution, InstitutionType } from '../../services/institution.service';
import { Group } from '../../services/group.service';

export enum PeerSelectorType {
  all = 0,
  institution = 1,
  group = 2
}

@RegisterComponent('PeerSelector')
@Component({
  selector: 'app-peer-selector',
  standalone: true,
  imports: [CommonModule, FormsModule, InstitutionAutocompleteComponent],
  templateUrl: './peer-selector.component.html',
  styleUrl: './peer-selector.component.scss'
})
export class PeerSelectorComponent {
  institutionAutocomplete = viewChild('institutionAutocomplete', { read: InstitutionAutocompleteComponent });
  @Input() type: PeerSelectorType = PeerSelectorType.all;
  @Input() maxInstitutions = 3;

  private readonly stateService = inject(StateService);
  readonly institution = this.stateService.institution;
  readonly groups = this.stateService.groups;
  readonly peer = this.stateService.peer;
  readonly group = this.stateService.group;
  readonly peerInstitution = this.stateService.peerInstitution;
  readonly peerInstitutions = this.stateService.peerInstitutions;
  private _peerInstitutions: Institution[] = [];
  
  customGroups: Group[] = [];
  vbcGroups: Group[] = [];
  aggregateGroups: Group[] = [];
  openCategories: string[] = [];

  isMasterChecked = false;
  filteredGroupCount = 0;
  isFilterOpen = false;
  isFilterDirect = false;

  isOpen = false;
  isClosing = false;

  PeerSelectorType = PeerSelectorType;
  institutionType = InstitutionType.all;

  readonly peerName = computed(() => {
    const peerInstitutions = this.peerInstitutions();
    switch (this.type) {
      case PeerSelectorType.institution:
        if (peerInstitutions.length == 0) {
          return 'Select Institutions';
        } else if (peerInstitutions.length == 1) {
          return peerInstitutions[0].label;
        } else {
          return 'Multiple Institutions Selected';
        }
      case PeerSelectorType.group:
        return this.group()?.name;
      default:
        return this.peer()?.institution?.label ?? this.peer()?.group?.name;
    }
  });

  readonly displayPeer = computed(() => {
    const peer = this.peer();
    const group = this.group();
    this.filteredGroupCount = group?.institutions.filter(g => g.isSelected).length ?? 0;
    this.isMasterChecked = this.filteredGroupCount == group?.institutions.length;

    switch (this.type) {
      case PeerSelectorType.institution:
        return { institution: peer?.institution, group: undefined };
      case PeerSelectorType.group:
        return { institution: undefined, group: group };
      default:
        return peer;
    }
  });

  constructor() {
    effect(() => {
      const groups = this.groups();
      this.customGroups = groups.filter(g => g.groupType == 10);
      this.vbcGroups = groups.filter(g => g.groupType == 20);
      this.aggregateGroups = groups.filter(g => g.groupType == 30);
      this._peerInstitutions = this.peerInstitutions();
    });
  }

  public openModal(): void {
    this.isOpen = true;
  }

  public closeModal(): void {
    if (this.isFilterOpen && !this.isFilterDirect) {
      this.isFilterOpen = false;
      setTimeout(() => {
        this.isClosing = true;
        setTimeout(() => {
          this.isOpen = false;
          this.isClosing = false;
        }, 600);
      }, 600);
    }
    else {
      this.isFilterOpen = false;
      this.isFilterDirect = false;
      this.isClosing = true;
      setTimeout(() => {
        this.isOpen = false;
        this.isClosing = false;
      }, 600);
    }
  }

  public selectInstitution(institution: Institution): void {
    if (this.type == PeerSelectorType.institution) {
      if (this._peerInstitutions.length < this.maxInstitutions) {
        this._peerInstitutions.push(institution);
        this.stateService.setPeerInstitutions(this._peerInstitutions);
      }
    }
    else {
      this.stateService.setPeer({ institution: institution, group: undefined });
    }
    this.closeModal();
  }

  public removeInstitution(institution: Institution): void {
    this._peerInstitutions = this._peerInstitutions.filter(i => i.iid != institution.iid);
    this.stateService.setPeerInstitutions(this._peerInstitutions);
  }

  public selectGroup(groupId: string): void {
    const group = this.groups().find(g => g.groupId == groupId);
    this.stateService.setFilteredGroup(group?.institutions ?? []);
    this.stateService.setPeer({ institution: undefined, group: group });
    this.closeModal();
  }

  public filterGroup(): void {
    this.isFilterOpen = true;
  }

  public filter(): void {
    this.isFilterOpen = true;
    this.isFilterDirect = true;
  }

  selectMaster() {
    this.group()?.institutions.forEach(i => {
      i.isSelected = this.isMasterChecked;
    });
    this.filteredGroupCount = this.group()?.institutions.filter(g => g.isSelected).length ?? 0;
    this.stateService.setFilteredGroup(this.group()?.institutions ?? []);
  }

  filterInstitution() {
    this.isMasterChecked = this.group()?.institutions.every(i => {
      return i.isSelected;
    }) ?? false;
    this.filteredGroupCount = this.group()?.institutions.filter(g => g.isSelected).length ?? 0;
    this.stateService.setFilteredGroup(this.group()?.institutions ?? []);
  }

  isCategoryOpen(category: string) {
    return this.openCategories.find(c => c == category);
  }

  toggleCategory(category: string) {
    if (this.openCategories.find(c => c == category)) {
      this.openCategories = this.openCategories.filter(c => c != category);
    }
    else {
      this.openCategories.push(category);
    }
  }
}
